import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Lock, User as UserIcon, Loader2 } from 'lucide-react';
import { useAdminAuth } from '../../lib/AdminAuth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';

const AdminLogin = () => {
  const { user, loading, login } = useAdminAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ username: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
      </div>
    );
  }

  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(form.username, form.password);
      toast({ title: 'Berhasil masuk!', description: 'Selamat datang, admin.' });
    } catch (err) {
      toast({
        title: 'Login gagal',
        description: err?.response?.data?.detail || 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#D4A94F]/[0.06] blur-[100px] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-werdi-media.png" alt="Werdi Media" className="w-16 h-16 object-contain mb-4" />
          <h1 className="text-white font-bold text-2xl tracking-tight">Werdi Media CMS</h1>
          <p className="text-neutral-500 text-sm mt-1">Admin Content Management</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#0d0d0d] border border-white/[0.06] rounded-3xl p-8"
        >
          <div className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">
                Username
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="admin"
                  autoComplete="username"
                  className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] focus-visible:border-[#D4A94F] text-white h-12 rounded-xl pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] focus-visible:border-[#D4A94F] text-white h-12 rounded-xl pl-10"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold btn-transition gold-shimmer hover:shadow-[0_10px_40px_-10px_rgba(212,169,79,0.5)]"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Masuk'}
            </Button>
          </div>
        </form>

        <p className="text-center text-neutral-600 text-xs mt-6">
          Panel admin ini terpisah dari{' '}
          <a
            href="https://admin.werdimedia.com"
            target="_blank"
            rel="noreferrer"
            className="text-[#D4A94F] hover:underline"
          >
            admin.werdimedia.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
